import { useLocation, useNavigate } from "react-router-dom";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { useContext, useEffect, useMemo, useState } from "react";
import { PreferencesContext } from "../../../PreferenceContext";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import {
  CreateRefHistoryOBGyHistoryTypeOfSurgery,
  GetRefHistoryOBGyHistoryTypeOfSurgeryDetailByID,
  UpdateRefHistoryOBGyHistoryTypeOfSurgery,
} from "../../../services/RefHistoryOBGyHistoryTypeOfSurgery";
import { AlertType } from "../../../services/models/AlertType";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import CustomBreadcrumbs from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import { LoadingButton } from "@mui/lab";
import { CreateRefHistoryOBGyHistoryOutcomeOfSurgery, GetRefHistoryOBGyHistoryOutcomeOfSurgeryDetailByID, UpdateRefHistoryOBGyHistoryOutcomeOfSurgery } from "../../../services/RefHistoryOBGyHistoryOutcomeOfSurgery";
// Schema for the form
interface formInitialValueTypes {
  refHistoryOBGyHistoryOutcomeOfSurgeryId: number;
  consumerId: number;
  organizationId: number;
  branchId: number;
  departmentId: number;
  specialityId: number;
  outcomeOfSurgeryName: string;
  code: string;
  isLocked: boolean;
  isDeleted: boolean;
}

export default function RefHistoryOBGyHistoryOutcomeOfSurgerySave(props: any) {
  const { isForPopUp = false, handleClosePopUp, name } = props;

  // Initial schema value for form
  const initlRefOutcomeOfSurgeryData: formInitialValueTypes = {
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    departmentId: 0,
    specialityId: 0,
    isDeleted: false,
    refHistoryOBGyHistoryOutcomeOfSurgeryId: 0,
    outcomeOfSurgeryName: isForPopUp ? name?.toUpperCase() : "",
    isLocked: false,
    code: "",
  };

  // Hooks and context
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // Getting params from URL
  let urlParams = useQuery();
  let curRawRefOutcomeOfSurgeryId = urlParams.get("q");
  let curRefOutcomeOfSurgeryId = 0;

  if (
    curRawRefOutcomeOfSurgeryId !== null &&
    curRawRefOutcomeOfSurgeryId !== undefined &&
    !isForPopUp
  ) {
    let decId = edService.decrypt(curRawRefOutcomeOfSurgeryId);
    if (decId) {
      curRefOutcomeOfSurgeryId = +decId;
    }
  }

  // States for loading and error handling
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  // Schema validation
  const validationSchema = Yup.object().shape({
    refHistoryOBGyHistoryOutcomeOfSurgeryId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    organizationId: Yup.number().required(),
    branchId: Yup.number().required(),
    departmentId: Yup.number().required(),
    specialityId: Yup.number().required(),
    outcomeOfSurgeryName: Yup.string().required(
      "Outcome of Surgery Name is required"
    ),
    code: Yup.string(),
    isLocked: Yup.boolean(),
    isDeleted: Yup.boolean(),
  });

  // Form hook
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initlRefOutcomeOfSurgeryData,
  });

  // Fetch ref Outcome of surgery detail
  const getRefOutcomeOfSurgeryDetail = async () => {
    let params = new URLSearchParams();
    params.append(
      "refHistoryOBGyHistoryOutcomeOfSurgeryId",
      curRefOutcomeOfSurgeryId?.toString()
    );
    params.append("consumerId", userContext?.consumerId.toString());
    params.append("organizationId", userContext?.organizationId.toString());
    params.append("branchId", userContext?.branchId.toString());
    params.append("departmentId", userContext?.departmentId.toString());
    params.append("specialityId", userContext?.specialityId.toString());
    try {
      const res: any = await GetRefHistoryOBGyHistoryOutcomeOfSurgeryDetailByID(
        params
      );
      const OutcomeOfSurgery = res.data.data;
      methods.reset({
        ...initlRefOutcomeOfSurgeryData,
        refHistoryOBGyHistoryOutcomeOfSurgeryId:
          OutcomeOfSurgery?.refHistoryOBGyHistoryOutcomeOfSurgeryId || 0,
        code: OutcomeOfSurgery?.code || "",
        outcomeOfSurgeryName: OutcomeOfSurgery?.outcomeOfSurgeryName || "",
      });
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    }
  };

  // Save and update Ref Outcome Of Surgery
  const saveRefOutcomeOfSurgery = async (value: formInitialValueTypes) => {
    let result: any = "";
    let formData = {
      refHistoryOBGyHistoryOutcomeOfSurgeryId:
        value.refHistoryOBGyHistoryOutcomeOfSurgeryId,
      specialityId: userContext?.specialityId,
      departmentId: userContext?.departmentId,
      branchId: userContext?.branchId,
      organizationId: userContext?.organizationId,
      consumerId: userContext?.consumerId,
      outcomeOfSurgeryName: value?.outcomeOfSurgeryName?.trim(),
      code: value.code?.trim(),
      isLocked: value.isLocked,
      isDeleted: value.isDeleted,
      DataManagedBy: userContext.loginDetail.userId,
    };
    try {
      if (curRefOutcomeOfSurgeryId > 0) {
        result = await UpdateRefHistoryOBGyHistoryOutcomeOfSurgery(formData);
      } else {
        result = await CreateRefHistoryOBGyHistoryOutcomeOfSurgery(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          handleClosePopUp("OUTCOME_OF_SURGERY", result?.data);
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    }
  };

  // On form submit
  const onSubmit = async (data: any) => {
    saveRefOutcomeOfSurgery(data);
  };

  // Handle back action
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // Fetch data when component mounts or ref illness item id changes
  const fetchData = async () => {
    if (curRefOutcomeOfSurgeryId > 0) {
      await getRefOutcomeOfSurgeryDetail();
    }
    setIsLoading(false);
  };
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curRefOutcomeOfSurgeryId]);

  // Handle skeleton visibility
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50);
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <Grid container className="page-header">
            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CustomBreadcrumbs />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}>
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={isForPopUp ? 12 : 6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.outcomeOfSurgeryName
                            ? "validation-message"
                            : ""
                        }`}>
                        Outcome of Surgery *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input">
                        <TextField
                          type="text"
                          autoCapitalize="none"
                          fullWidth
                          size="small"
                          autoFocus
                          {...methods.register("outcomeOfSurgeryName", {
                            onChange: (e) => {
                              //CONVERT INPUT INTO UPPERCASE LETTERS
                              const input = e.target;
                              e.target.value = input.value
                              ? input.value.toUpperCase()
                              : " ";

                              //required: true;
                            },
                          })}
                          error={!!methods.formState.errors.outcomeOfSurgeryName}
                          className={`text-input ${
                            methods.formState.errors.outcomeOfSurgeryName
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.outcomeOfSurgeryName?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    variant="contained"
                    type="submit"
                    name="save"
                    disabled={showSkeleton}>
                    {curRefOutcomeOfSurgeryId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    variant="outlined"
                    type="button"
                    name="cancel"
                    onClick={handleBack}>
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
